import { useState } from 'react';
import type { AuditLegalHoldItem, AuditRetentionClass } from '../../api/adminOps';
import {
  useExtendAuditLegalHold,
  usePlaceAuditLegalHold,
  useReleaseAuditLegalHold,
} from '../../hooks/useAdminOps';
import {
  HoldAction,
  LegalHoldStatus,
  RETENTION_CLASS_OPTIONS,
  ReferenceLabel,
  extendHoldRequest,
  placeHoldRequest,
  releaseHoldRequest,
  retentionClassFrom,
} from '../auditRetention.logic';
import { BlockerHint, PartitionsField, ReferenceField } from './parts';

const SECOND_ADMIN_NOTE = 'A second admin must approve this in Approvals before it takes effect.';

export function PlaceHoldModal({ onClose }: { onClose: () => void }) {
  const place = usePlaceAuditLegalHold();
  const [caseId, setCaseId] = useState('');
  const [approvalRef, setApprovalRef] = useState('');
  const [partitionsText, setPartitionsText] = useState('');
  const { blocker, payload } = placeHoldRequest({ caseId, approvalRef, partitionsText });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Place legal hold"
        className="glass-card w-full max-w-lg p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold text-white">Place legal hold</h2>
        <p className="text-sm text-gray-text">
          Held partitions are never pruned, whatever their retention class, until the hold is
          released. {SECOND_ADMIN_NOTE}
        </p>
        <ReferenceField
          id="audit-hold-place-case"
          label={ReferenceLabel.CASE}
          value={caseId}
          onChange={setCaseId}
        />
        <ReferenceField
          id="audit-hold-place-approval"
          label={ReferenceLabel.APPROVAL}
          value={approvalRef}
          onChange={setApprovalRef}
        />
        <PartitionsField
          id="audit-hold-place-partitions"
          label="Partitions"
          value={partitionsText}
          onChange={setPartitionsText}
        />
        <BlockerHint blocker={blocker} />
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary disabled:opacity-50"
            disabled={payload === null || place.isPending}
            onClick={
              payload === null ? undefined : () => place.mutate(payload, { onSuccess: onClose })
            }
          >
            {place.isPending ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtendHoldForm({
  hold,
  onCancel,
  onDone,
}: {
  hold: AuditLegalHoldItem;
  onCancel: () => void;
  onDone: () => void;
}) {
  const extend = useExtendAuditLegalHold();
  const [approvalRef, setApprovalRef] = useState('');
  const [partitionsText, setPartitionsText] = useState('');
  const { blocker, payload } = extendHoldRequest(hold, { approvalRef, partitionsText });

  return (
    <div className="space-y-3">
      <ReferenceField
        id="audit-hold-extend-approval"
        label={ReferenceLabel.APPROVAL}
        value={approvalRef}
        onChange={setApprovalRef}
      />
      <PartitionsField
        id="audit-hold-extend-partitions"
        label="Partitions to add"
        value={partitionsText}
        onChange={setPartitionsText}
      />
      {payload !== null && (
        <p className="text-xs text-gray-text">
          Adds {payload.partitions.join(', ')}. {SECOND_ADMIN_NOTE}
        </p>
      )}
      <BlockerHint blocker={blocker} />
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-primary flex-1 disabled:opacity-50"
          disabled={payload === null || extend.isPending}
          onClick={
            payload === null
              ? undefined
              : () => extend.mutate({ holdId: hold.hold_id, payload }, { onSuccess: onDone })
          }
        >
          {extend.isPending ? 'Submitting…' : 'Request extension'}
        </button>
      </div>
    </div>
  );
}

function ReleaseHoldForm({
  hold,
  onCancel,
  onDone,
}: {
  hold: AuditLegalHoldItem;
  onCancel: () => void;
  onDone: () => void;
}) {
  const release = useReleaseAuditLegalHold();
  const [approvalRef, setApprovalRef] = useState('');
  const [retentionClass, setRetentionClass] = useState<AuditRetentionClass | null>(null);
  const { blocker, payload } = releaseHoldRequest(hold, { approvalRef, retentionClass });

  return (
    <div className="space-y-3">
      <ReferenceField
        id="audit-hold-release-approval"
        label={ReferenceLabel.APPROVAL}
        value={approvalRef}
        onChange={setApprovalRef}
      />
      <div>
        <label className="label" htmlFor="audit-hold-release-class">
          Retention after release
        </label>
        <select
          id="audit-hold-release-class"
          className="input w-full"
          value={retentionClass ?? ''}
          onChange={event => setRetentionClass(retentionClassFrom(event.target.value))}
        >
          <option value="">Choose a retention class…</option>
          {RETENTION_CLASS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-text">
        Once released, the held partitions can be pruned again under the chosen retention class.{' '}
        {SECOND_ADMIN_NOTE}
      </p>
      <BlockerHint blocker={blocker} />
      <div className="flex gap-2">
        <button className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={payload === null || release.isPending}
          onClick={
            payload === null
              ? undefined
              : () => release.mutate({ holdId: hold.hold_id, payload }, { onSuccess: onDone })
          }
        >
          {release.isPending ? 'Submitting…' : 'Request release'}
        </button>
      </div>
    </div>
  );
}

export function HoldActions({
  hold,
  canWrite,
  onDone,
}: {
  hold: AuditLegalHoldItem;
  canWrite: boolean;
  onDone: () => void;
}) {
  const [action, setAction] = useState<HoldAction | null>(null);
  const cancel = () => setAction(null);

  if (hold.status !== LegalHoldStatus.ACTIVE) {
    return (
      <p className="text-xs text-gray-text">This hold was released and can no longer be changed.</p>
    );
  }
  if (!canWrite) {
    return (
      <p className="text-xs text-gray-text">
        Read only: extending or releasing an audit legal hold needs the admin role.
      </p>
    );
  }
  if (action === HoldAction.EXTEND) {
    return <ExtendHoldForm hold={hold} onCancel={cancel} onDone={onDone} />;
  }
  if (action === HoldAction.RELEASE) {
    return <ReleaseHoldForm hold={hold} onCancel={cancel} onDone={onDone} />;
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <button className="btn-secondary" onClick={() => setAction(HoldAction.EXTEND)}>
        Extend hold
      </button>
      <button className="btn-ghost text-red-400" onClick={() => setAction(HoldAction.RELEASE)}>
        Release hold
      </button>
    </div>
  );
}
